import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import styles from './ai-prompt-box-suggestions.module.scss';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {TextSize} from '@Components/text';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {IconProps} from '@Components/icon-v2';
import {Icon} from '@Components/icon-v2';
import {motion} from 'framer-motion';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {
  AiPromptBoxSuggestionsProps,
  PromptSuggestionPillAlignment,
  PromptSuggestionPillPosition,
  Suggestion,
} from '@Components/ai-prompt-box/components/ai-prompt-box-prompt-suggestions/ai-prompt-box-prompt-suggestions.types';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';

const RECENT_PROMPT_DISPLAY_LENGTH = 30;
const RECENT_PROMPT_SUGGESTION_PILL_KEY = 'recent-prompt-suggestion-pill';
const RECENT_PROMPT_SUGGESTION_PILL_ICON = 'icon-history';

export function AiPromptBoxSuggestions(props: AiPromptBoxSuggestionsProps): ReactElement {
  const {state, actions} = useAiPromptBox(props.id);
  const promptSuggestions = state.promptSuggestions;
  const [visibleSuggestions, setVisibleSuggestions] = useState<Suggestion[]>([]);
  const [hiddenIndices, setHiddenIndices] = useState<number[]>([]);

  const pillSizeClassMap = {
    [AiPromptBoxSize.SMALL]: PillButtonSize.SMALL,
    [AiPromptBoxSize.MEDIUM]: PillButtonSize.MEDIUM,
    [AiPromptBoxSize.SINGLE_LINE]: PillButtonSize.SMALL,
    [AiPromptBoxSize.SMALL_EXTENDED]: PillButtonSize.SMALL,
  };

  const textSizeClassMap = {
    [AiPromptBoxSize.SMALL]: TextSize.XXSMALL,
    [AiPromptBoxSize.MEDIUM]: TextSize.XSMALL,
    [AiPromptBoxSize.SINGLE_LINE]: TextSize.XXSMALL,
    [AiPromptBoxSize.SMALL_EXTENDED]: TextSize.XXSMALL,
  };

  const refreshIconSizeMap = {
    [AiPromptBoxSize.SMALL]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.MEDIUM]: IconSize.SIZE_ICON_20,
    [AiPromptBoxSize.SINGLE_LINE]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.SMALL_EXTENDED]: IconSize.SIZE_ICON_16,
  };

  useEffect(() => {
    if (!promptSuggestions) {
      return;
    }

    const maxVisible = promptSuggestions.maxVisibleSuggestions ?? promptSuggestions.suggestions.length;
    const initial = promptSuggestions.suggestions.slice(0, maxVisible);
    setVisibleSuggestions(initial);
  }, [promptSuggestions?.suggestions, promptSuggestions?.maxVisibleSuggestions]);

  const handlePromptChange = (prompt: string): void => {
    actions.update({prompt});
  };

  const getSuggestionPill = (key: string, displayText: string, replacementText: string, iconProps?: IconProps, onClickCallback?: () => void): ReactElement => {
    return (
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <PillButton
          key={key}
          text={displayText}
          size={pillSizeClassMap[state.size]}
          textSize={textSizeClassMap[state.size]}
          leftIconProps={iconProps}
          onClick={(_id, e) => {
            e?.stopPropagation();
            handlePromptChange(replacementText);
            onClickCallback?.();
          }}
        />
      </div>
    );
  };

  const getPromptSuggestionPills = (): ReactElement[] => {
    if (!visibleSuggestions || visibleSuggestions.length === 0) {
      return [];
    }

    return visibleSuggestions.map((suggestion: Suggestion) => {
      const displayText = `${suggestion.emoji} ${suggestion.title}`;

      return getSuggestionPill(suggestion.prompt, displayText, suggestion.prompt, undefined, undefined);
    });
  };

  const getRecentPromptPillDisplayText = (originalText: string): string => {
    if (originalText.length <= RECENT_PROMPT_DISPLAY_LENGTH) {
      return originalText;
    }

    return originalText.slice(0, RECENT_PROMPT_DISPLAY_LENGTH) + '...';
  };

  const getRecentPrompt = (): ReactElement | undefined => {
    if (!promptSuggestions?.recentPrompt) {
      return;
    }

    return getSuggestionPill(
      RECENT_PROMPT_SUGGESTION_PILL_KEY,
      getRecentPromptPillDisplayText(promptSuggestions.recentPrompt),
      promptSuggestions.recentPrompt,
      {
        icon: RECENT_PROMPT_SUGGESTION_PILL_ICON,
        size: IconSize.SIZE_ICON_16,
        className: 'spacing-m-r-1',
      },
      undefined
    );
  };

  const handleRefreshClick = (): void => {
    if (!promptSuggestions || visibleSuggestions.length === 0) {
      return;
    }

    const allSuggestions = promptSuggestions.suggestions;
    const maxVisible = promptSuggestions.maxVisibleSuggestions ?? allSuggestions.length;
    const currentIndices = visibleSuggestions.map((vis) => allSuggestions.findIndex((sug) => sug.prompt === vis.prompt));
    const newHiddenIndices = [...hiddenIndices, ...currentIndices];
    const availableIndices = allSuggestions.map((_, idx) => idx).filter((idx) => !newHiddenIndices.includes(idx));

    if (availableIndices.length < maxVisible) {
      const shuffled = [...allSuggestions].sort(() => Math.random() - 0.5);
      setVisibleSuggestions(shuffled.slice(0, maxVisible));
      setHiddenIndices([]);
    } else {
      const nextVisible = availableIndices.slice(0, maxVisible).map((idx) => allSuggestions[idx]);
      setVisibleSuggestions(nextVisible);
      setHiddenIndices(newHiddenIndices);
    }
  };

  const getRefreshIcon = (): ReactElement | undefined => {
    if (!promptSuggestions?.showRefreshButton) {
      return;
    }

    return (
      <Icon
        type={IconType.SECONDARY}
        shape={IconShape.CIRCLE}
        size={refreshIconSizeMap[state.size]}
        icon={promptSuggestions?.refreshIcon ?? 'icon-rotate-ccw'}
        ariaLabel={window.i18next.t('pmwjs_refresh')}
        onClick={handleRefreshClick}
      />
    );
  };

  const getAlignmentClass = (): string => {
    switch (promptSuggestions?.align) {
      case PromptSuggestionPillAlignment.CENTER:
        return 'flex-row-justify-center';
      case PromptSuggestionPillAlignment.RIGHT:
        return 'flex-justify-end';
      case PromptSuggestionPillAlignment.LEFT:
      default:
        return 'flex-row-justify-start';
    }
  };

  const getSuggestions = (): ReactElement => {
    const animationY = promptSuggestions?.position === PromptSuggestionPillPosition.ABOVE ? 8 : -8;

    return (
      <motion.div
        style={{overflow: 'hidden', zIndex: 1}}
        initial={{opacity: 0, height: 0, y: animationY}}
        animate={{opacity: 1, height: 'auto', y: 0}}
        exit={{opacity: 0, height: 0, y: animationY}}
        transition={{duration: 0.2, ease: 'easeInOut'}}
      >
        <div className={`${styles.promptSuggestions} ${getAlignmentClass()}`}>
          {getRecentPrompt()}
          {getPromptSuggestionPills()}
          {getRefreshIcon()}
        </div>
      </motion.div>
    );
  };

  return getSuggestions();
}
